import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ordersApi, productsApi } from '../api/client';
import Card from '../components/Card';
import Button from '../components/Button';
import ConfirmDialog from '../components/ConfirmDialog';
import Input from '../components/Input';

// Функция форматирования количества: целые числа без десятичной части, дробные с одним знаком
const formatQty = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0';
  return num % 1 === 0 ? num.toString() : num.toFixed(1);
};

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [paidAmountInput, setPaidAmountInput] = useState('');

  const { data: order, isLoading, error: orderError } = useQuery({
    queryKey: ['order', id],
    queryFn: async () => {
      const response = await ordersApi.getById(id);
      return response.data;
    },
    enabled: !!id,
  });

  const issueMutation = useMutation({
    mutationFn: () => ordersApi.markAsIssued(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['order', id]);
      queryClient.invalidateQueries(['orders']);
      queryClient.invalidateQueries(['stock']);
      alert('Заказ отмечен как выдан!');
    },
    onError: (error) => {
      const errorData = error.response?.data;
      const errorMessage = errorData?.details || errorData?.error || 'Ошибка при отметке заказа';
      alert(errorMessage);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => ordersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['orders']);
      queryClient.invalidateQueries(['stock']);
      queryClient.invalidateQueries(['products']);
      alert('Заказ успешно удален!');
      navigate('/orders');
    },
    onError: (error) => {
      alert(error.response?.data?.error || 'Ошибка при удалении заказа');
      setShowDeleteConfirm(false);
    },
  });

  const paymentMutation = useMutation({
    mutationFn: (paidAmount) => ordersApi.updatePayment(id, paidAmount),
    onSuccess: () => {
      queryClient.invalidateQueries(['order', id]);
      queryClient.invalidateQueries(['orders']);
      setPaidAmountInput('');
      alert('Оплата успешно обновлена!');
    },
    onError: (error) => {
      alert(error.response?.data?.error || 'Ошибка при обновлении оплаты');
    },
  });

  // Очищаем поле ввода при загрузке заказа
  useEffect(() => {
    if (order) {
      setPaidAmountInput('');
    }
  }, [order]);

  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const response = await productsApi.getAll();
      return response.data;
    },
  });

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="text-center py-8">Загрузка...</div>
      </div>
    );
  }

  if (orderError) {
    return (
      <div className="p-4">
        <div className="text-center py-8 text-red-600">
          Ошибка загрузки заказа: {orderError.message || 'Неизвестная ошибка'}
        </div>
        <button
          onClick={() => navigate('/orders')}
          className="text-blue-600 mt-4"
        >
          ← Назад к заказам
        </button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-4">
        <div className="text-center py-8">Заказ не найден</div>
        <button
          onClick={() => navigate('/orders')}
          className="text-blue-600 mt-4"
        >
          ← Назад к заказам
        </button>
      </div>
    );
  }

  const getProductName = (productId) => {
    const product = products?.find(p => p.id === productId);
    return product?.name || `Товар #${productId}`;
  };

  const date = order.date ? new Date(order.date) : new Date();
  const isIssued = order.is_issued === true || order.is_issued === 1;

  return (
    <div className="p-4">
      <div className="mb-4">
        <button
          onClick={() => navigate('/orders')}
          className="text-blue-600 mb-4"
        >
          ← Назад
        </button>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {(order.is_preorder === true || order.is_preorder === 1) ? 'Предзаказ' : 'Заказ'} #{order.id}
        </h1>
        <p className="text-gray-600 mb-2">
          {(order.is_preorder === true || order.is_preorder === 1) ? 'Создан: ' : ''}
          {date.toLocaleDateString('ru-RU')} {date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
        </p>
        {order.issued_date && (
          <p className="text-green-600 font-medium mb-2">
            Выдан: {new Date(order.issued_date).toLocaleDateString('ru-RU')} {new Date(order.issued_date).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
          </p>
        )}
        {!isIssued && (
          <Button
            variant="success"
            size="md"
            onClick={() => {
              const isPreorder = order.is_preorder === true || order.is_preorder === 1;
              if (confirm(`Отметить ${isPreorder ? 'предзаказ' : 'заказ'} как выдан?`)) {
                issueMutation.mutate();
              }
            }}
            disabled={issueMutation.isPending}
            className="mt-2"
          >
            {issueMutation.isPending ? 'Обработка...' : '✓ Отметить как выдан'}
          </Button>
        )}
        {isIssued && (
          <span className="inline-block mt-2 px-3 py-1 bg-green-100 text-green-700 text-sm font-semibold rounded">
            ✓ Выдан
          </span>
        )}
      </div>

      {/* Информация о покупателе */}
      {(order.customer_name || order.instagram || order.phone || order.delivery_address) && (
        <Card className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Покупатель</h2>
          <div className="space-y-2">
            {order.customer_name && (
              <div>
                <span className="text-sm text-gray-500">Имя:</span>
                <p className="text-base font-medium text-gray-900">{order.customer_name}</p>
              </div>
            )}
            {order.instagram && (
              <div>
                <span className="text-sm text-gray-500">Инстаграм:</span>
                <p className="text-base text-gray-900">{order.instagram}</p>
              </div>
            )}
            {order.phone && (
              <div>
                <span className="text-sm text-gray-500">Телефон:</span>
                <p className="text-base text-gray-900">{order.phone}</p>
              </div>
            )}
            {order.delivery_address && (
              <div>
                <span className="text-sm text-gray-500">Адрес получателя:</span>
                <p className="text-base text-gray-900">{order.delivery_address}</p>
              </div>
            )}
            {(order.is_preorder === true || order.is_preorder === 1) && (
              <div className="mt-2">
                <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">
                  📋 Предзаказ
                </span>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Товары */}
      <Card className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Товары</h2>
        {order.items && Array.isArray(order.items) && order.items.length > 0 ? (
          <>
            <div className="space-y-2">
              {order.items.map((item) => {
                if (!item) return null;
                const itemTotal = (parseFloat(item.price || 0) * parseFloat(item.qty || 0));
                const product = products?.find(p => p.id === item.product_id);
                return (
                  <div key={item.id || item.product_id} className="p-3 bg-gray-50 rounded">
                    <div className="flex justify-between items-start mb-1">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">
                          {getProductName(item.product_id)}
                        </h3>
                        {product?.item_code && (
                          <p className="text-xs font-bold text-blue-600 mt-1 font-mono">
                            Артикул: {product.item_code}
                          </p>
                        )}
                        {item.dioptry !== null && item.dioptry !== undefined && item.dioptry !== '' && (
                          <p className="text-xs text-blue-600 font-medium mt-1">
                            Диоптрии: {item.dioptry === 0 || item.dioptry === '0' ? '0' : "-" + parseFloat(item.dioptry).toFixed(2)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">
                        {formatQty(item.qty || 0)} × {parseFloat(item.price || 0).toFixed(2)} ₸
                      </span>
                      <span className="font-bold text-blue-600">
                        {itemTotal.toFixed(2)} ₸
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-900">Общая сумма:</span>
                <span className="text-xl font-bold text-blue-600">
                  {parseFloat(order.total_amount || 0).toFixed(2)} ₸
                </span>
              </div>
              
              {/* Информация об оплате */}
              <div className="pt-3 border-t border-gray-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Оплачено:</span>
                  <span className={`text-lg font-bold ${
                    parseFloat(order.paid_amount || 0) >= parseFloat(order.total_amount || 0)
                      ? 'text-green-600'
                      : 'text-orange-600'
                  }`}>
                    {parseFloat(order.paid_amount || 0).toFixed(2)} ₸
                  </span>
                </div>
                {parseFloat(order.paid_amount || 0) < parseFloat(order.total_amount || 0) && (
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm text-gray-600">Остаток к оплате:</span>
                    <span className="text-lg font-bold text-red-600">
                      {(parseFloat(order.total_amount || 0) - parseFloat(order.paid_amount || 0)).toFixed(2)} ₸
                    </span>
                  </div>
                )}
                {parseFloat(order.paid_amount || 0) >= parseFloat(order.total_amount || 0) && (
                  <div className="mb-3">
                    <span className="inline-block px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                      ✓ Оплачено полностью
                    </span>
                  </div>
                )}
                
                {/* Форма для внесения оплаты */}
                <div className="mt-3">
                  <Input
                    label="Внести оплату"
                    type="number"
                    step="0.01"
                    min="0"
                    max={parseFloat(order.total_amount || 0) - parseFloat(order.paid_amount || 0)}
                    value={paidAmountInput}
                    onChange={(e) => setPaidAmountInput(e.target.value)}
                    placeholder="0.00"
                  />
                  <Button
                    variant="primary"
                    size="md"
                    className="w-full mt-2"
                    onClick={() => {
                      const amount = parseFloat(paidAmountInput);
                      if (isNaN(amount) || amount <= 0) {
                        alert('Введите корректную сумму');
                        return;
                      }
                      const currentPaid = parseFloat(order.paid_amount || 0);
                      const newPaidAmount = currentPaid + amount;
                      const totalAmount = parseFloat(order.total_amount || 0);
                      
                      if (newPaidAmount > totalAmount) {
                        alert(`Сумма оплаты не может превышать общую сумму заказа (${totalAmount.toFixed(2)} ₸)`);
                        return;
                      }
                      
                      paymentMutation.mutate(newPaidAmount);
                    }}
                    disabled={paymentMutation.isPending || !paidAmountInput || parseFloat(paidAmountInput) <= 0}
                  >
                    {paymentMutation.isPending ? 'Сохранение...' : 'Внести оплату'}
                  </Button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <p className="text-gray-500">В заказе нет товаров</p>
        )}
      </Card>

      {/* Кнопка удаления */}
      <div className="mt-4">
        <Button
          variant="danger"
          size="lg"
          className="w-full"
          onClick={() => setShowDeleteConfirm(true)}
          disabled={deleteMutation.isPending}
        >
          {deleteMutation.isPending ? 'Удаление...' : '🗑️ Удалить заказ'}
        </Button>
      </div>

      {deleteMutation.error && (
        <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
          {deleteMutation.error.response?.data?.error || 'Ошибка при удалении заказа'}
        </div>
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Подтвердите удаление"
        message={`Вы уверены, что хотите удалить ${(order?.is_preorder === true || order?.is_preorder === 1) ? 'предзаказ' : 'заказ'} #${order?.id}? ${!order?.is_preorder || order?.is_issued ? 'Товары будут возвращены на склад.' : ''} Это действие нельзя отменить.`}
        confirmText="Удалить"
        cancelText="Отмена"
        variant="danger"
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
};

export default OrderDetail;
